// THE WORKSPACE'S PROJECTS IN AN AI ANSWER — the `canvas-projects` view
// (functions/canvasAnswer.js canvasAiViews; the shell mounts it in the
// assistant's thread and on a Fly page with scope { params, embedded }).
// Read-only: the shared ProjectCard grid the home Projects widget and /studio
// draw. A card opens the project's LIVE PREVIEW (the sheet; ⌘ / Ctrl: a
// split), never the canvas origin; its row of actions (CanvasProjectsCard)
// opens the preview, the canvas or the analytics; its "Open in Websites"
// shortcut the Websites board. Its projects live in its OWN state, read once
// per params. While reading: three card stand-ins; none, or a failed read:
// one sentence.
export const CanvasProjectsView = {
  flow: 'y',
  gap: 'A',
  minWidth: '0',
  dataCanvasProjects: true,
  state: { projects: null, total: 0, failed: false },
  // EMPTY (the views contract, workspace functions/assistantAppViews.js): the
  // read answered with no project — the one sentence is all the view shows,
  // and a Fly page gives its card one row. null while reading.
  empty: (el, s) => (Array.isArray(s.projects) ? s.projects.length === 0 : null),
  onRender: (el) => el.call('canvasAnswerLoad', el),

  CanvasProjectsSkeleton: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, var(--spacing-F2)), 1fr))',
    gap: 'A',
    ariaBusy: 'true',
    hide: (el, s) => Array.isArray(s.projects),
    Skeleton_1: { height: 'E', round: 'radiusControl' },
    Skeleton_2: { height: 'E', round: 'radiusControl' },
    Skeleton_3: { height: 'E', round: 'radiusControl' }
  },

  CanvasProjectsNote: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z',
    color: 'caption',
    hide: (el, s) => !Array.isArray(s.projects) || s.projects.length > 0,
    text: (el, s) => {
      void s.root.lang
      return el.call('polyglot', s.failed ? 'canvas.answer.failed' : 'canvas.answer.empty')
    }
  },

  CanvasProjectsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, var(--spacing-F2)), 1fr))',
    gap: 'A',
    minWidth: '0',
    hide: (el, s) => !Array.isArray(s.projects) || !s.projects.length,
    childExtends: 'CanvasProjectsCard',
    childrenAs: 'state',
    children: (el, s) => (Array.isArray(s.projects) ? s.projects : [])
  },

  // The newest N of more: says so, never a silent cut — and nothing at all for
  // an empty view (one rule, functions/canvasAnswer.js canvasAnswerMoreText).
  CanvasProjectsMore: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z',
    color: 'caption',
    hide: (el, s) => !el.call('canvasAnswerMoreText', s),
    text: (el, s) => {
      void s.root.lang
      return el.call('canvasAnswerMoreText', s)
    }
  }
}

// One project of the view: the shared ProjectCard (its whole face opens the
// project's live preview, in the environment the view names) and, under it,
// the read-only actions the Studio card offers — Preview, Open canvas,
// Analytics (QA 2026-10-18: a Fly tile offered only "Open in Apps"). Its
// state is the card row (functions/canvasAnswer.js canvasAnswerCards).
export const CanvasProjectsCard = {
  flow: 'y',
  gap: 'X',
  minWidth: '0',

  ProjectCard: {
    ariaLabel: (el, s) => {
      void s.root.lang
      return String(el.call('polyglot', 'canvas.answer.action.previewOf')).replace(
        '{name}',
        () => s.name || s.key || ''
      )
    },
    onClick: (e, el, s) => {
      // The Studio button stops its own click; the guard is the base card's.
      if (e.target?.closest?.('[data-studio-btn]')) return
      el.call('canvasAnswerOpen', s, 'preview', e)
    }
  },

  CanvasProjectsCardActions: {
    flow: 'x',
    align: 'center',
    gap: 'X',
    minWidth: '0',
    childExtends: 'CanvasProjectsCardAction',
    // Keys that name no HTML tag and no registered component (`Canvas` would
    // be both).
    OpenPreview: { icon: 'eye', openAs: 'preview', labelKey: 'canvas.answer.action.preview' },
    OpenCanvas: { icon: 'layers', openAs: 'canvas', labelKey: 'canvas.answer.action.canvas' },
    OpenAnalytics: {
      icon: 'analytics',
      openAs: 'analytics',
      labelKey: 'canvas.answer.action.analytics'
    }
  }
}

// One action of a project card: the kit's square icon control, its name in
// the accessible label and the tooltip.
export const CanvasProjectsCardAction = {
  extends: 'CompactIconButton',
  ariaLabel: (el, s) => {
    void s.root.lang
    return String(el.call('polyglot', el.labelKey || '')).replace(
      '{name}',
      () => s.name || s.key || ''
    )
  },
  title: (el, s) => {
    void s.root.lang
    return String(el.call('polyglot', el.labelKey || '')).replace(
      '{name}',
      () => s.name || s.key || ''
    )
  },
  onClick: (e, el, s) => {
    e.stopPropagation()
    el.call('canvasAnswerOpen', s, el.openAs, e)
  }
}
